'use client';

import { changePassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState } from 'react';
import { FiEyeOff, FiEye } from 'react-icons/fi';
import { Field } from '../form/labeled-field';
export const changePasswordSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      password_old: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type ChangePasswordFormProps = {
  onSubmitSuccess?: () => void;
};

export function ChangePasswordForm(props: ChangePasswordFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof changePasswordSchema>>>({
    resolver: zodResolver(changePasswordSchema(formValidationMessages)),
    defaultValues: {
      password_old: '',
      password: '',
      password_confirmation: '',
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const { toast } = useToast();
  const router = useRouter();
  const authToken = useCustomerStore(s => s.me!.authToken);

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      /** TODO: preklad */
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    if (data.password_old === data.password) {
      /** TODO: preklad */
      form.setError('password', { type: 'manual', message: t('text1103') });
      return;
    }

    try {
      await changePassword({ ...data, authToken, locale });
      toast({
        title: t('text649'),
        variant: 'success',
      });
      props.onSubmitSuccess?.();
      router.push(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text650'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showPasswordOld, setShowPasswordOld] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  const handlePasswordOldMouseDown = () => {
    setShowPasswordOld(true);
  };

  const handlePasswordOldMouseUp = () => {
    setShowPasswordOld(false);
  };

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col gap-4 sm:gap-5 lg:gap-6', 'ea-change-password-form')}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'flex w-full max-w-[400px] flex-col gap-3',
            'ea-change-password-form__fields',
          )}
        >
          <Field
            name="password_old"
            type="password"
            label={t('text202')}
            control={form.control}
            required
          />
          <Field
            name="password"
            type="password"
            label={t('text191')}
            control={form.control}
            required
          />
          <Field
            name="password_confirmation"
            type="password"
            label={t('text191')}
            control={form.control}
            required
          />
        </div>
        <Button
          type="submit"
          className={cn('w-full sm:w-fit', 'ea-change-password-form__submit-button')}
          isLoading={form.formState.isSubmitting}
        >
          {t('text187')}
        </Button>
      </form>
    </Form>
  );
}
